import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../contexts/ToastContext";
import { translateTenantError } from "../utils/errorTranslator";
import {
  getTenants,
  getTenant,
  createTenant,
  updateTenant,
  deleteTenant,
  updateTenantModules,
  addUserToTenant,
  removeUserFromTenant,
  type Tenant,
  type CreateTenantPayload,
  type UpdateTenantPayload,
  type AddUserToTenantPayload,
} from "../services/api";
import { UpdateTenantResult } from "../services/tenantApi";
import {
  syncStripeSubscriptions,
  SyncStripeSubscriptionsResponse,
} from "../services/accountService";

// Query keys
export const TENANT_KEYS = {
  all: ["tenants"] as const,
  lists: () => [...TENANT_KEYS.all, "list"] as const,
  list: (filters?: Record<string, any>) =>
    [...TENANT_KEYS.lists(), filters] as const,
  details: () => [...TENANT_KEYS.all, "detail"] as const,
  detail: (id: string) => [...TENANT_KEYS.details(), id] as const,
};

// Get all tenants
export const useTenantsQuery = () => {
  return useQuery({
    queryKey: TENANT_KEYS.list(),
    queryFn: getTenants,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Get single tenant
export const useTenantQuery = (tenantId: string) => {
  return useQuery({
    queryKey: TENANT_KEYS.detail(tenantId),
    queryFn: () => getTenant(tenantId),
    enabled: !!tenantId,
  });
};

// Create tenant
export const useCreateTenantMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (tenantData: CreateTenantPayload) => createTenant(tenantData),
    onSuccess: (newTenant) => {
      // Update the list cache with the new tenant
      queryClient.setQueryData<Tenant[]>(TENANT_KEYS.list(), (old) => {
        return old ? [...old, newTenant] : [newTenant];
      });

      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: TENANT_KEYS.lists() });

      addToast({
        message: "Tenant został utworzony pomyślnie",
        type: "success",
      });
    },
    onError: (error: any) => {
      console.error("Error creating tenant:", error);
      addToast({
        message: translateTenantError(error, "create"),
        type: "error",
      });
    },
  });
};

// Update tenant
export const useUpdateTenantMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      tenantId,
      tenantData,
    }: {
      tenantId: string;
      tenantData: UpdateTenantPayload;
      /** Skip the generic success toast — caller shows a more specific one (e.g. marketing opt-out, AC-12). */
      silent?: boolean;
    }) => updateTenant(tenantId, tenantData),
    onSuccess: (result: UpdateTenantResult, { tenantId, silent }) => {
      // Extract tenant from result
      const updatedTenant = result.tenant;

      // Update the specific tenant in cache
      queryClient.setQueryData(TENANT_KEYS.detail(tenantId), updatedTenant);

      // Update the tenant in the list cache
      queryClient.setQueryData<Tenant[]>(TENANT_KEYS.list(), (old) => {
        return old
          ? old.map((tenant) =>
              tenant.id === tenantId ? updatedTenant : tenant
            )
          : [updatedTenant];
      });

      if (!silent) {
        addToast({
          message: "Tenant został zaktualizowany pomyślnie",
          type: "success",
        });
      }
    },
    onError: (error: any) => {
      console.error("Error updating tenant:", error);
      addToast({
        message: translateTenantError(error, "update"),
        type: "error",
      });
    },
  });
};

// Delete tenant
export const useDeleteTenantMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (tenantId: string) => deleteTenant(tenantId),
    onSuccess: (_, tenantId) => {
      // Remove the tenant from list cache
      queryClient.setQueryData<Tenant[]>(TENANT_KEYS.list(), (old) => {
        return old ? old.filter((tenant) => tenant.id !== tenantId) : [];
      });

      // Remove the specific tenant cache
      queryClient.removeQueries({ queryKey: TENANT_KEYS.detail(tenantId) });

      addToast({
        message: "Tenant został usunięty pomyślnie",
        type: "success",
      });
    },
    onError: (error: any) => {
      console.error("Error deleting tenant:", error);
      addToast({
        message: translateTenantError(error, "delete"),
        type: "error",
      });
    },
  });
};

// Update tenant modules
export const useUpdateTenantModulesMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      tenantId,
      enabledModules,
    }: {
      tenantId: string;
      enabledModules: string[];
    }) => updateTenantModules(tenantId, enabledModules),
    onSuccess: (updatedTenant, { tenantId }) => {
      // Update caches
      queryClient.setQueryData(TENANT_KEYS.detail(tenantId), updatedTenant);
      queryClient.setQueryData<Tenant[]>(TENANT_KEYS.list(), (old) => {
        return old
          ? old.map((tenant) =>
              tenant.id === tenantId ? updatedTenant : tenant
            )
          : [updatedTenant];
      });

      addToast({
        message: "Moduły tenanta zostały zaktualizowane",
        type: "success",
      });
    },
    onError: (error: any) => {
      console.error("Error updating tenant modules:", error);
      addToast({
        message: translateTenantError(error, "updateModules"),
        type: "error",
      });
    },
  });
};

// Add user to tenant
export const useAddUserToTenantMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      tenantId,
      userData,
    }: {
      tenantId: string;
      userData: AddUserToTenantPayload;
    }) => addUserToTenant(tenantId, userData),
    onSuccess: (_, { tenantId }) => {
      // Invalidate the specific tenant to refresh user assignments
      queryClient.invalidateQueries({ queryKey: TENANT_KEYS.detail(tenantId) });
      queryClient.invalidateQueries({ queryKey: TENANT_KEYS.lists() });

      addToast({
        message: "Użytkownik został dodany do tenanta",
        type: "success",
      });
    },
    onError: (error: any) => {
      console.error("Error adding user to tenant:", error);
      addToast({
        message: translateTenantError(error, "addUser"),
        type: "error",
      });
    },
  });
};

// Remove user from tenant
export const useRemoveUserFromTenantMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ tenantId, userId }: { tenantId: string; userId: string }) =>
      removeUserFromTenant(tenantId, userId),
    onSuccess: (_, { tenantId }) => {
      // Invalidate the specific tenant to refresh user assignments
      queryClient.invalidateQueries({ queryKey: TENANT_KEYS.detail(tenantId) });
      queryClient.invalidateQueries({ queryKey: TENANT_KEYS.lists() });

      addToast({
        message: "Użytkownik został usunięty z tenanta",
        type: "success",
      });
    },
    onError: (error: any) => {
      console.error("Error removing user from tenant:", error);
      addToast({
        message: translateTenantError(error, "removeUser"),
        type: "error",
      });
    },
  });
};

// Sync Stripe Subscriptions
export const useSyncStripeSubscriptionsMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: () => syncStripeSubscriptions(),
    onSuccess: (result: SyncStripeSubscriptionsResponse) => {
      // Invalidate tenants list to refresh subscription data
      queryClient.invalidateQueries({ queryKey: TENANT_KEYS.lists() });

      // Show success message with statistics
      const message = `Synchronizacja zakończona: ${result.successfulSyncs} sukces, ${result.skipped} pominięte, ${result.failures} błędy`;
      addToast({
        message,
        type: result.failures > 0 ? "error" : "success",
      });
    },
    onError: (error: any) => {
      console.error("Error syncing Stripe subscriptions:", error);
      addToast({
        message: error.message || "Błąd synchronizacji subskrypcji Stripe",
        type: "error",
      });
    },
  });
};
